/**
 * PlayerController — the ONE owner of the player lifecycle.
 *
 * v1 had ~40 boolean flags mutated by three competing watchdogs, any of which
 * could dispose and rebuild the player. Here there is a single explicit state,
 * every transition goes through transition() (and is logged), and nothing
 * outside this class may create or destroy the player.
 *
 *   boot ──▶ resolving ──▶ playing ◀──▶ resolving (source switch / recovery)
 *                │  ▲          │
 *                ▼  │          ▼
 *               offline ◀──────┘
 */

import { buildConfig, type PlayerConfig } from './config'
import * as api from './api'
import { recallSession, rememberSession } from './ids'
import {
  canSignOwnRequests,
  createPlayer,
  onCastSdkReady,
  setMediaToken,
  type PlayerHandle,
} from './player'
import { autoplayPolicy, getSoundPref, setSoundPref } from './prefs'
import { OverlayManager } from './overlays'
import { mountPasswordPrompt, type PasswordPromptHandle } from './password-prompt'
import { ChatPanel } from './chat'

export type State = 'boot' | 'resolving' | 'playing' | 'offline' | 'denied' | 'stopped'
type Source = 'primary' | 'fallback'

const STATUS_POLL_MS = 4000
const VIEWERS_POLL_MS = 4000
/** Presentation config poll. Conditional on an ETag, so a no-op costs a 304. */
const CONFIG_POLL_MS = 30_000
/**
 * A policy denial is not always final. The broadcaster can add the domain,
 * widen the geo list or re-enable the stream from the dashboard while the
 * viewer is still sitting on the blocked card, and a concurrency denial lifts
 * on its own the moment a slot frees. So the player keeps asking.
 *
 * It backs off, because most denials never lift and a page left open on a
 * blocked embed must not become a steady stream of authorize calls.
 */
const DENIAL_RECHECK_MS = 10_000
const DENIAL_RECHECK_MAX_MS = 5 * 60_000
/**
 * How long a change that needs a re-attach may wait for a natural seam before
 * we take the interruption anyway. Correctness eventually beats smoothness.
 */
const DEFERRED_CONFIG_MAX_WAIT_MS = 5 * 60_000
/**
 * Fields the public config endpoint does not carry, by design: per-viewer or
 * dev-only. They come from the boot authorize and must survive every poll.
 */
const CARRIED_OVER_FROM_AUTHORIZE = [
  'nsid',
  'fallbackEmbedId',
  'preloadHlsUrl',
  'backupHlsUrl',
] as const satisfies readonly (keyof PlayerConfig)[]
const WATCHDOG_MS = 8000
const WATCHDOG_STRIKES = 2
const SWITCH_TO_PRIMARY_DELAY_MS = 30_000 // primary must be back this long before we leave fallback
const SWITCH_TO_FALLBACK_DELAY_MS = 3_000 // leave a dead primary quickly
const OFFLINE_GRACE_MS = 5_000
/** Unanswered status polls tolerated before we admit we are offline. */
const STATUS_FAILURES_BEFORE_OFFLINE = 3 // both sources down this long => offline UI
const MAX_REBUILD_DELAY_MS = 15_000
/**
 * Media errors tolerated before we stop retrying and say so. A blip deserves a
 * silent retry; a wall deserves an explanation. Retrying forever behind a
 * spinner is how a player comes to look dead while it is still working.
 */
const MAX_MEDIA_ERRORS = 4
/** Errors absorbed silently before the viewer is told we are reconnecting. */
const QUIET_MEDIA_ERRORS = 1
const PREVIEW_REFRESH_MS = 10_000
/** Nothing in a background preview may block longer than this. */
const PREVIEW_PLAY_TIMEOUT_MS = 6_000
/** How long the viewer may stare at the starting ring before we rebuild. */
const STARTING_STALL_MS = 8_000
/** Long enough for a double-click (which never plays) to have resolved. */
const DOUBLE_CLICK_SETTLE_MS = 800
/* How many settle windows the ring may be held across while a resolve or a
   preview is still in flight. Bounded so it always expires before the
   starting-stall timer takes over (8 x 800ms = 6.4s < STARTING_STALL_MS). */
const STARTING_SETTLE_RETRIES = 8

/**
 * Does this gesture mean "I want to watch", or is it someone operating the
 * chrome?
 *
 * It used to not matter: the control bar was dead before first play, so the
 * only thing a pointer could land on was the picture or the big play button.
 * Now the bar is live in every not-playing state, and reaching for the volume
 * or the chat toggle during a background preview refresh would have been read
 * as a request to start playing, and would have started it.
 */
function isPlayGesture(target: EventTarget | null): boolean {
  const el = target instanceof Element ? target : null
  // Keyboard with nothing focused, or a synthetic event: treat as the picture.
  if (!el) return true
  if (el.closest('.vjs-big-play-button, .vjs-play-control')) return true
  return !el.closest('.vjs-control-bar, .vjs-menu, .lp-chat, .lp-top-controls, .lp-context-menu')
}
/** Rebuild attempts before we stop trying to start playback on our own. */
const MAX_STARTING_REBUILDS = 2
/** Backoff when the renewal call itself cannot reach the service. */
const RENEW_RETRY_MS = 30_000 // how often a paused player re-syncs to live

interface PendingSwitch {
  target: Source
  timer: ReturnType<typeof setTimeout>
}

/**
 * play() that always settles.
 *
 * video.js queues a play() made before its source is ready and, if the
 * readiness events land in the wrong order, never runs the queued call — the
 * promise then hangs forever. Inside the preview flows that would strand
 * `priming`/`refreshing` at true, which silently disables the watchdog, the
 * preview refresher and the offline detector for the rest of the session.
 */
async function playBounded(handle: PlayerHandle): Promise<void> {
  await Promise.race([
    (async () => {
      await handle.whenPlayable()
      await handle.player.play()
    })(),
    new Promise<void>((resolve) => setTimeout(resolve, PREVIEW_PLAY_TIMEOUT_MS)),
  ])
}

/** Branded copy for each refusal. The offline card already takes an override. */
/**
 * Denials worth re-asking about. `iframe_*` describes how the page embedded
 * the player, which cannot change without the page itself reloading, and
 * `password_*` belongs to the gate, which is waiting on the viewer rather than
 * on the server.
 */
const RECHECKABLE_DENIALS = new Set<api.DenialReason>([
  'domain_not_allowed',
  'geo_blocked',
  'ip_blocked',
  'concurrency_exceeded',
  'stream_disabled',
])

function denialCopy(
  reason: api.DenialReason,
  message: string,
): { label: string; title: string; subtitle: string } {
  switch (reason) {
    case 'geo_blocked':
      return {
        label: 'Unavailable',
        title: 'Not available in your region',
        subtitle: message || 'The broadcaster has restricted where this stream can be watched.',
      }
    case 'ip_blocked':
      return {
        label: 'Unavailable',
        title: 'This network cannot watch this stream',
        subtitle: message || 'Access is limited to approved networks.',
      }
    case 'domain_not_allowed':
      return {
        label: 'Blocked',
        title: 'This player is not authorised on this site',
        subtitle: message || 'The broadcaster has limited where this stream can be embedded.',
      }
    case 'iframe_required':
      return {
        label: 'Blocked',
        title: 'This stream can only be watched where it is embedded',
        subtitle: message || 'Open it on the page that hosts it.',
      }
    case 'iframe_forbidden':
      return {
        label: 'Blocked',
        title: 'This stream cannot be embedded here',
        subtitle: message || 'Open it directly instead.',
      }
    case 'concurrency_exceeded':
      return {
        label: 'At capacity',
        title: 'This stream is at its viewer limit',
        subtitle: message || 'Please try again in a few minutes.',
      }
    case 'stream_disabled':
    default:
      return {
        label: 'Unavailable',
        title: 'This stream is unavailable',
        subtitle: message || 'Please check back later.',
      }
  }
}

export class PlayerController {
  private config: PlayerConfig
  private readonly overlays: OverlayManager
  private readonly container: HTMLElement

  private state: State = 'boot'
  private handle: PlayerHandle | null = null

  private primaryLive = false
  private fallbackLive = false
  private activeSource: Source | null = null
  private sessionId: string | null = null

  private userPaused = false
  private hasBeenUnmuted = false
  private rebuildCount = 0
  private mediaErrorCount = 0
  private renewedForAuthError = false
  private sawMediaAuthError = false
  private resolveBusy = false
  /** Consecutive status polls that never reached the API */
  private statusFailures = 0
  /** currentTime when the viewer pressed play — the starting state ends when
   *  playback moves PAST it, not merely when the element claims it is ready */
  private startingFrom = 0
  private startingArmedAt = 0
  private startingStallTimer: ReturnType<typeof setTimeout> | null = null
  /** Rebuild attempts for a playback that never starts (bounded) */
  private startingStallRebuilds = 0
  /** Mute/volume to restore if a preview refresh is cut short by the viewer */
  private refreshRestore: { muted: boolean; volume: number } | null = null
  /** True during the muted prime-and-pause frame preview */
  private priming = false
  /** True during a background live-preview refresh (paused player) */
  private refreshing = false
  /** After a preview, the next user-initiated play should start at the live edge */
  private resumeFromLiveEdge = false
  /** The viewer has started playback themselves at least once */
  private hasStartedOnce = false
  /** Media has actually rendered in THIS viewer's session at least once.
   *  Distinct from `hasStartedOnce`, which several recovery paths set
   *  optimistically so a rebuilt player resumes without a second click. */
  private everPlayed = false
  /** Any source has reported live at least once in this session. Separates
   *  "the broadcast ended" (deserves the grace window) from "it never
   *  started" (deserves nothing but the offline screen). */
  private sawLiveOnce = false

  /** The grant the boot authorize produced, consumed by the first resolve */
  private pendingGrant: api.PlaybackGrant | null = null
  private configEtag: string | null = null
  private stopConfigPoller: (() => void) | null = null
  /** A config change that needs a re-attach, held until a natural seam. */
  private pendingConfig: PlayerConfig | null = null
  private pendingConfigTimer: ReturnType<typeof setTimeout> | null = null
  private unwatchCastSdk: (() => void) | null = null
  private renewTimer: ReturnType<typeof setTimeout> | null = null
  private passwordGate: PasswordPromptHandle | null = null
  /** Built from the first grant, never for a viewer who was refused. */
  private chat: ChatPanel | null = null
  /** The denial currently on screen, and the recheck that may lift it. */
  private deniedReason: api.DenialReason | null = null
  private denialRecheckTimer: ReturnType<typeof setTimeout> | null = null
  private denialRecheckDelay = DENIAL_RECHECK_MS
  private denialRecheckBusy = false
  private lastDenialRecheckAt = 0
  /** Set by a renewal while playing; picked up on the next rebuild */
  private renewedUrl: string | null = null

  private pendingSwitch: PendingSwitch | null = null
  private offlineGraceTimer: ReturnType<typeof setTimeout> | null = null
  private restartTimer: ReturnType<typeof setTimeout> | null = null

  private stopStatusPoller: (() => void) | null = null
  private stopViewersPoller: (() => void) | null = null
  private watchdogTimer: ReturnType<typeof setInterval> | null = null
  private previewTimer: ReturnType<typeof setInterval> | null = null
  private lastProbeTime = -1
  private watchdogStrikes = 0

  constructor(
    container: HTMLElement,
    config: PlayerConfig,
    overlays: OverlayManager,
    /** From the boot authorize — reused so booting costs ONE call, not two */
    grant?: api.PlaybackGrant | null,
  ) {
    this.container = container
    this.config = config
    this.overlays = overlays
    if (grant) this.adoptGrant(grant)
  }

  /** Record a fresh grant: remember the session and schedule the renewal. */
  private adoptGrant(grant: api.PlaybackGrant): void {
    this.pendingGrant = grant
    this.sessionId = grant.sessionId
    // Segments carry the token too, so a renewal has to reach the request hook
    // immediately — not on the next rebuild. Doing it here makes renewal
    // invisible: the running player simply starts signing with the new token,
    // no source swap and no interruption to the viewer.
    setMediaToken(grant.hlsUrl)
    // The SESSION's clock, never the token's: a 30-minute token expiring must
    // not make a returning viewer retype the password.
    rememberSession(this.config.embedId, grant.sessionId, grant.sessionExpiresAt)
    this.ensureChat(grant.sessionId)
    this.chat?.setSession(grant.sessionId)
    this.scheduleRenewal(grant)
  }

  /** Adopt a grant produced outside boot (e.g. after the password gate). */
  adoptBootGrant(grant: api.PlaybackGrant): void {
    this.adoptGrant(grant)
  }

  /** Render a refusal the boot call already made, without starting anything. */
  renderDenial(reason: api.DenialReason, message: string): void {
    // Boot was refused, so start() never runs and nothing else wires these up.
    // Without them the blocked card is terminal: the viewer would have to
    // reload to find out the broadcaster had fixed the policy.
    this.attachWakeListeners()
    this.goDenied(reason, message)
  }

  // ── lifecycle ────────────────────────────────────────────────────────────

  start(): void {
    this.log(`start embed=${this.config.embedId}`)

    // Cap the status poller's error backoff: a viewer waiting on a stalled
    // player should not have to wait out an ever-doubling retry to be told
    // what is happening.
    this.startPollers()

    // Coming back from sleep or a dropped connection, the poller may be deep
    // in its backoff (up to 8x the interval). These are the moments the viewer
    // is most likely to be looking at the player, so re-check at once.
    this.attachWakeListeners()

    // If the Cast SDK lands after the player mounted, the cast tech was never
    // registered and there is no cast button. Rebuilding fixes that, but only
    // while nobody is watching yet — interrupting live playback to add a button
    // is a worse trade than making them press play once more later.
    this.unwatchCastSdk = onCastSdkReady(() => {
      if (this.config.audioOnly || this.state === 'stopped') return
      if (this.state === 'playing' && !this.handle?.probe().paused) return
      this.log('cast SDK arrived after mount — rebuilding to pick up the cast tech')
      void this.resolveAndPlay(this.activeSource ?? 'primary')
    })

    // A boot grant is already an answer: watch-guard resolved this embed and
    // handed back a playable URL, so there is nothing to wait for. Holding the
    // mount until the first liveness poll returns would add a round trip to
    // first frame, and would leave the viewer on a blank shell whenever the
    // status API is slow — which it is allowed to be, because it is an
    // advisory signal about the broadcast, not the authority on playback.
    if (this.config.preloadHlsUrl || this.pendingGrant) {
      void this.resolveAndPlay('primary')
    }
  }

  /**
   * The viewers ping is also the liveness signal for concurrency counting, so
   * it must not run for a viewer who was refused — otherwise a denied viewer
   * holds a slot they never got.
   */
  private startPollers(): void {
    this.stopPollers()
    // Cap the status poller's error backoff: a viewer waiting on a stalled
    // player should not have to wait out an ever-doubling retry to be told
    // what is happening.
    this.stopStatusPoller = api.startPoller(() => this.pollStatus(), {
      intervalMs: STATUS_POLL_MS,
      maxBackoffMs: STATUS_POLL_MS * 2,
    })
    this.stopViewersPoller = api.startPoller(() => this.pollViewers(), { intervalMs: VIEWERS_POLL_MS })
    this.stopConfigPoller = api.startPoller(() => this.pollConfig(), { intervalMs: CONFIG_POLL_MS })
    this.watchdogTimer = setInterval(() => this.watchdogTick(), WATCHDOG_MS)
    if (this.config.keepPreviewFresh) {
      this.previewTimer = setInterval(() => void this.refreshLivePreview(), PREVIEW_REFRESH_MS)
    }
  }

  private stopPollers(): void {
    this.stopStatusPoller?.()
    this.stopViewersPoller?.()
    this.stopConfigPoller?.()
    this.stopStatusPoller = null
    this.stopViewersPoller = null
    this.stopConfigPoller = null
    if (this.watchdogTimer) clearInterval(this.watchdogTimer)
    if (this.previewTimer) clearInterval(this.previewTimer)
    this.watchdogTimer = null
    this.previewTimer = null
  }

  /** Idempotent: addEventListener ignores a repeat of the same reference. */
  private attachWakeListeners(): void {
    window.addEventListener('online', this.onWake)
    document.addEventListener('visibilitychange', this.onWake)
  }

  private onWake = (): void => {
    if (this.state === 'stopped') return
    if (document.visibilityState === 'hidden') return
    if (this.state === 'denied') {
      // Returning to the tab is the likeliest moment for the viewer to be
      // looking at it, so jump the backoff — but never below the floor, or a
      // flapping background tab turns into a request loop.
      if (Date.now() - this.lastDenialRecheckAt >= DENIAL_RECHECK_MS) void this.recheckDenial()
      return
    }
    void this.pollStatus()
  }

  stop(): void {
    this.transition('stopped', 'stop() called')
    window.removeEventListener('online', this.onWake)
    document.removeEventListener('visibilitychange', this.onWake)
    this.unwatchCastSdk?.()
    this.unwatchCastSdk = null
    if (this.pendingConfigTimer) clearTimeout(this.pendingConfigTimer)
    this.pendingConfigTimer = null
    this.pendingConfig = null
    this.stopPollers()
    this.passwordGate?.dispose()
    this.passwordGate = null
    this.chat?.dispose()
    this.chat = null
    this.clearTimers()
    this.destroyPlayer()
  }

  private transition(to: State, reason: string): void {
    if (this.state === to) return
    this.log(`state ${this.state} -> ${to} (${reason})`)
    this.state = to

    // The chat follows the STATE, not any one code path into it. Offline is
    // reached from several places — the status poller, the offline grace
    // timer, a run of media errors that gives up — and hooking them
    // individually left the panel up in whichever one was added last.
    //
    // Offline is also the only state that takes the chat away. Booting,
    // resolving and buffering keep it: a room that vanishes while the video is
    // still loading reads as breakage rather than as an ended broadcast. So
    // does casting, which never leaves the playing state — the video moves to
    // the receiver, the conversation stays on the device in the viewer's hand.
    if (to === 'offline') this.chat?.setStreamOffline(true)
    else if (to === 'resolving' || to === 'playing') this.chat?.setStreamOffline(false)
  }

  // ── live status ──────────────────────────────────────────────────────────

  private async pollStatus(): Promise<boolean> {
    if (this.state === 'stopped') return true

    const wasEitherLive = this.primaryLive || this.fallbackLive

    let reachedApi = true

    if (this.config.preloadHlsUrl) {
      this.primaryLive = true // dev override: pretend live
    } else {
      const primary = await api.fetchLiveStatus(this.config.embedId)
      // null = the status API could not be reached. Keep the last known state
      // instead of inventing "offline": a viewer whose laptop slept, whose
      // wifi dropped for a moment, or whose request timed out has NOT been
      // told the stream ended, and telling them so is a lie we then have to
      // recover from.
      if (primary === null) reachedApi = false
      else this.primaryLive = primary
    }
    if (this.config.fallbackEmbedId) {
      const fallback = await api.fetchLiveStatus(this.config.fallbackEmbedId)
      if (fallback === null) reachedApi = false
      else this.fallbackLive = fallback
    }

    // An unanswered poll decides nothing on its own — a blip, a sleeping
    // laptop, one slow response. A RUN of them, or a browser that says it has
    // no network at all, is a different thing: the viewer is disconnected and
    // deserves to be told, rather than left with a play button that spins.
    if (!reachedApi) {
      this.statusFailures++
      const noNetwork = navigator.onLine === false
      this.log(
        `status unreachable (${this.statusFailures}/${STATUS_FAILURES_BEFORE_OFFLINE}` +
          `${noNetwork ? ', browser reports no network' : ''})`,
      )
      if (noNetwork || this.statusFailures >= STATUS_FAILURES_BEFORE_OFFLINE) {
        this.goDisconnected()
      }
      return false
    }
    this.statusFailures = 0

    const eitherLive = this.primaryLive || this.fallbackLive
    // The badge follows the broadcast, not the player. An unanswered poll
    // keeps the last known state, so this keeps it too rather than
    // flickering the badge off on one slow response.
    this.overlays.setStreamLive(eitherLive)

    // Any source alive cancels a scheduled "give up".
    if (eitherLive && this.offlineGraceTimer) {
      clearTimeout(this.offlineGraceTimer)
      this.offlineGraceTimer = null
    }

    if (eitherLive) this.sawLiveOnce = true

    if (eitherLive && (this.state === 'boot' || this.state === 'offline')) {
      void this.resolveAndPlay(this.primaryLive ? 'primary' : 'fallback')
    } else if (
      !eitherLive &&
      !this.sawLiveOnce &&
      this.state !== 'offline' &&
      this.state !== 'denied'
    ) {
      // This used to test `state === 'boot'`, which was too narrow. Boot does
      // not wait for the first status poll: an authorize grant mounts the
      // player straight away, so by the time the poll lands the state is
      // 'resolving' and `this.handle` is set. The branch below then takes it,
      // and that branch only acts on a live-to-not-live transition, which has
      // not happened because nothing was ever live. The player stayed mounted
      // on a stream that had not started, showing a big play button, and only
      // a media error eventually put a card over the top of it.
      this.goOffline(this.state === 'boot' ? 'nothing live at boot' : 'nothing live, nothing ever was')
    } else if (this.state === 'playing' || this.handle) {
      // `this.handle` covers a mounted-but-not-yet-playing player: a pre-start
      // embed sitting on its preview frame is just as entitled to be told the
      // stream ended as one that is actually rendering.
      this.managePendingSwitch()
      if (wasEitherLive && !eitherLive && !this.offlineGraceTimer) {
        this.log(`both sources down — offline in ${OFFLINE_GRACE_MS}ms unless they return`)
        this.offlineGraceTimer = setTimeout(() => {
          this.offlineGraceTimer = null
          this.goOffline('offline grace expired')
        }, OFFLINE_GRACE_MS)
      }
    }

    return true
  }

  // ── live presentation config ─────────────────────────────────────────────

  /**
   * Changes that alter which URL we play, so they cannot be applied to a
   * running player. Everything else is text, colour or CSS and applies in place.
   */
  private static readonly SOURCE_AFFECTING = [
    'liveRewind',
    'rewindWindowSeconds',
    'fallbackEmbedId',
  ] as const satisfies readonly (keyof PlayerConfig)[]

  private async pollConfig(): Promise<boolean> {
    if (this.state === 'stopped' || this.state === 'denied') return true
    if (this.config.preloadHlsUrl) return true // dev override owns the config

    const result = await api.fetchConfig(this.config.embedId, this.configEtag)
    if (result.kind !== 'config') return result.kind === 'unchanged'

    this.configEtag = result.etag
    // Rebuild through the same path boot used, so defaults, colour validation
    // and the audio-only coupling behave identically to a fresh load.
    const next = buildConfig(result.config, {
      embedId: this.config.embedId,
      audioOnly: this.config.audioOnly,
    })
    // The public config deliberately omits per-viewer and dev-only fields, and
    // buildConfig fills an absent field with its default. Without carrying the
    // current values across, every poll would quietly null them — and because
    // fallbackEmbedId is source-affecting, that would schedule a re-attach that
    // drops the fallback source entirely.
    for (const key of CARRIED_OVER_FROM_AUTHORIZE) {
      Object.assign(next, { [key]: this.config[key] })
    }
    this.applyConfig(next)
    return true
  }

  /**
   * Apply what can be applied now; defer what would interrupt.
   *
   * The viewer should never pay more than the change costs. A renamed stream or
   * a new accent colour is a text node and a CSS variable. A theme swap is an
   * attribute. Only a different playlist needs the media torn down, and that
   * waits for a moment when nobody is mid-sentence.
   */
  private applyConfig(next: PlayerConfig): void {
    const prev = this.config
    const changed = (Object.keys(next) as (keyof PlayerConfig)[]).filter((k) => next[k] !== prev[k])
    if (changed.length === 0) return

    const deferred = changed.filter((k) =>
      (PlayerController.SOURCE_AFFECTING as readonly string[]).includes(k),
    )
    const immediate = changed.filter((k) => !deferred.includes(k))

    if (immediate.length) {
      this.log(`config changed: ${immediate.join(', ')}`)
      // Source-affecting fields keep their current values until the deferred
      // apply lands, so the player never disagrees with what it is playing.
      const applied = { ...next } as PlayerConfig
      for (const k of deferred) Object.assign(applied, { [k]: prev[k] })
      this.config = applied

      const { chromeNeedsRemount } = this.overlays.updateConfig(applied)
      if (chromeNeedsRemount && this.handle) this.remountChrome()
      if (immediate.includes('chatEnabled')) this.syncChat()
      // The poster is set on the video element at creation, so a poster the
      // broadcaster adds later never reached a player that was already up.
      if (immediate.includes('posterUrl')) this.applyPoster()
      // keepPreviewFresh owns a timer, so its poller needs restarting.
      if (immediate.includes('keepPreviewFresh')) this.startPollers()
    }

    if (deferred.length) {
      this.log(`config change needs a re-attach, waiting for a seam: ${deferred.join(', ')}`)
      this.pendingConfig = next
      this.maybeApplyPendingConfig()
      if (this.pendingConfig && !this.pendingConfigTimer) {
        this.pendingConfigTimer = setTimeout(() => {
          this.pendingConfigTimer = null
          this.log('deferred config waited long enough — applying now')
          this.adoptPendingConfig('now')
        }, DEFERRED_CONFIG_MAX_WAIT_MS)
      }
    }
  }

  /**
   * Safe only when nothing is mounted. A paused player looks like a seam but is
   * not one: applying there means either re-attaching, which resumes playback
   * the viewer deliberately stopped, or leaving the chrome describing a source
   * we are not playing.
   */
  private maybeApplyPendingConfig(): void {
    if (this.pendingConfig && !this.handle) this.adoptPendingConfig('idle')
  }

  /**
   * `inline`  we are already inside a resolve; adopt and let it build the URL
   * `idle`    nothing is mounted; adopt, the next resolve will use it
   * `now`     the wait expired; adopt and re-attach, interruption accepted
   */
  private adoptPendingConfig(mode: 'inline' | 'idle' | 'now'): void {
    const next = this.pendingConfig
    if (!next) return
    this.pendingConfig = null
    if (this.pendingConfigTimer) {
      clearTimeout(this.pendingConfigTimer)
      this.pendingConfigTimer = null
    }

    this.log(`applying deferred config (${mode})`)
    this.config = next
    const { chromeNeedsRemount } = this.overlays.updateConfig(next)
    if (chromeNeedsRemount && this.handle) this.remountChrome()
    this.syncChat()
    this.container.classList.toggle('with-rewind', next.liveRewind)

    if (mode === 'now') void this.resolveAndPlay(this.activeSource ?? 'primary')
  }

  /** Exactly one scheduled switch can exist; contrary status cancels it. */
  private managePendingSwitch(): void {
    const wantPrimary = this.activeSource === 'fallback' && this.primaryLive
    const wantFallback =
      this.activeSource === 'primary' && !this.primaryLive && this.fallbackLive && !!this.config.fallbackEmbedId

    if (this.pendingSwitch) {
      const stillValid =
        (this.pendingSwitch.target === 'primary' && wantPrimary) ||
        (this.pendingSwitch.target === 'fallback' && wantFallback)
      if (!stillValid) {
        this.log(`cancelling scheduled switch to ${this.pendingSwitch.target}`)
        clearTimeout(this.pendingSwitch.timer)
        this.pendingSwitch = null
      }
      return
    }

    const schedule = (target: Source, delay: number) => {
      this.log(`scheduling switch to ${target} in ${delay}ms`)
      this.pendingSwitch = {
        target,
        timer: setTimeout(() => {
          this.pendingSwitch = null
          void this.resolveAndPlay(target)
        }, delay),
      }
    }

    if (wantPrimary) schedule('primary', SWITCH_TO_PRIMARY_DELAY_MS)
    else if (wantFallback) schedule('fallback', SWITCH_TO_FALLBACK_DELAY_MS)
  }

  // ── source resolution & playback ─────────────────────────────────────────

  private async resolveAndPlay(preferred: Source): Promise<void> {
    if (this.resolveBusy || this.state === 'stopped') return
    this.resolveBusy = true
    // We are about to rebuild anyway — the cheapest possible moment to take a
    // change that needs a re-attach.
    this.adoptPendingConfig('inline')
    this.transition('resolving', `resolve ${preferred}`)

    try {
      const resolved = await this.resolveSource(preferred)
      if (!resolved) {
        this.goOffline('no playable source')
        return
      }

      const isSwitch = this.handle !== null || this.rebuildCount > 0
      // A renewal that landed while playing supersedes whatever we resolved.
      const url = this.renewedUrl ?? resolved.url
      this.renewedUrl = null
      this.destroyPlayer()
      this.attachPlayer(url, resolved.source, isSwitch)
    } finally {
      this.resolveBusy = false
    }
  }

  /**
   * A source is now whatever watch-guard authorises. There is no reachability
   * probe: the service is the authority on playability, and probing a tokenised
   * URL would cost a real (counted, cached) manifest fetch per resolve.
   */
  private async resolveSource(preferred: Source): Promise<{ url: string; source: Source } | null> {
    const candidates: Source[] = preferred === 'primary' ? ['primary', 'fallback'] : ['fallback', 'primary']

    for (const source of candidates) {
      const url = await this.playbackUrlFor(source)
      if (url) return { url: this.applyRewind(url, source), source }
      if (this.state === 'denied') return null // policy said no; stop looking
    }
    return null
  }

  private async playbackUrlFor(source: Source): Promise<string | null> {
    if (source === 'primary') {
      // Dev / rollback path: play an untokenised URL, no API involved.
      if (this.config.preloadHlsUrl) return this.config.preloadHlsUrl

      // The boot authorize already produced one — spend it rather than
      // authorizing twice and creating a second session.
      const pending = this.pendingGrant
      if (pending) {
        this.pendingGrant = null
        return pending.hlsUrl
      }

      const result = await api.authorize(this.config.embedId, {
        framed: window.self !== window.top,
        audioOnly: this.config.audioOnly,
        sessionId: recallSession(this.config.embedId),
      })
      return this.consumeAuthorize(result)
    }

    if (this.config.backupHlsUrl) return this.config.backupHlsUrl
    if (!this.config.fallbackEmbedId) return null

    // The fallback is authorised LAZILY, only when we actually switch to it:
    // each embed is its own policy evaluation and its own session, so doing it
    // eagerly would consume a second viewer slot against the customer's cap.
    const result = await api.authorize(this.config.fallbackEmbedId, {
      framed: window.self !== window.top,
      audioOnly: this.config.audioOnly,
    })
    return result.ok ? result.playback.hlsUrl : null
  }

  /** Turn an authorize result into a URL, or move to the matching state. */
  private consumeAuthorize(result: api.AuthorizeResult): string | null {
    if (result.ok) {
      this.adoptGrant(result.playback)
      this.pendingGrant = null
      return result.playback.hlsUrl
    }
    if (result.kind === 'denied') {
      this.goDenied(result.reason, result.message)
      return null
    }
    // Transport or 5xx: not a policy answer. Leave the state machine alone and
    // let the status poller retry — this is the same class as a dropped
    // connection, not a refusal.
    this.log(`authorize failed (status ${result.status ?? 'network'})`)
    return null
  }

  /**
   * DVR window is only offered on the primary source, and only to clients that
   * can sign their own requests.
   *
   * Flussonic names the DVR master and its media playlist identically
   * (rewind-3600.m3u8 at both levels), and the edge tells masters from variants
   * by filename alone. So the CDN's session token, which is what carries
   * authorisation to the child requests, is refused on the rewind variant: list
   * the name and the variant is misread as a master, omit it and the master is
   * never rewritten. Either way a client that only follows URIs gets a 403 one
   * hop in.
   *
   * MSE clients are fine, because the request hook re-signs every fetch. Native
   * HLS - Safari, and any cast receiver - has no such hook, so we hand it the
   * live playlist instead. It watches at the live edge without DVR scrubbing,
   * which is a smaller loss than a player that will not start.
   *
   * Remove this once the two playlists have distinct filenames; then the
   * session token works everywhere and rewind can be offered to everyone.
   */
  private applyRewind(url: string, source: Source): string {
    if (!this.config.liveRewind || source !== 'primary') return url
    if (!canSignOwnRequests()) {
      this.log('native HLS client — serving the live playlist, no DVR window')
      return url
    }
    this.container.classList.add('with-rewind')
    return url.replace('/index.m3u8', `/rewind-${this.config.rewindWindowSeconds}.m3u8`)
  }

  private attachPlayer(hlsUrl: string, source: Source, isSwitch: boolean): void {
    this.activeSource = source
    this.userPaused = false
    this.watchdogStrikes = 0
    this.lastProbeTime = -1

    // Rebuilds/switches keep whatever the viewer already chose. On first
    // creation, ask the browser what it will permit (Chrome/Firefox expose
    // getAutoplayPolicy) instead of firing a play() we expect to be rejected.
    // Where it's unsupported (Safari) we still attempt audible and fall back.
    let muted: boolean
    if (isSwitch) {
      muted = !this.hasBeenUnmuted
    } else {
      const policy = autoplayPolicy()
      muted = policy === 'allowed-muted' || policy === 'disallowed'
      if (policy !== 'unknown') this.log(`autoplay policy: ${policy}`)
    }

    this.log(`attach source=${source} muted=${muted} url=${hlsUrl.split('?')[0]}`)

    // Leaving the offline state is what clears the offline screen — NOT the
    // first rendered frame. A recovered stream that comes back to a paused
    // pre-start player (autoplay off, or autoplay blocked) never fires
    // `playing`, and the offline card would sit on top of a perfectly healthy
    // player, quietly fetching manifests behind it.
    this.overlays.hideOffline()

    this.handle = createPlayer({
      container: this.container,
      config: this.config,
      hlsUrl,
      sessionId: this.sessionId,
      muted,
      // Late-bound on purpose: the panel can be built or disposed by a config
      // poll long after this player was created.
      chat: {
        isAvailable: () => this.config.chatEnabled && !!this.chat,
        isOpen: () => this.chat?.isOpen() ?? false,
        toggle: () => this.chat?.toggle(),
      },
      events: {
        onPlaying: () => this.onPlaying(),
        onUserPause: () => this.onUserPause(),
        onError: (msg) => this.onPlayerError(msg),
        onHttpError: (url, status) => this.onHttpError(url, status),
        onAutoplayBlocked: () => this.onAutoplayBlocked(),
        onRemoteSessionEnded: () => this.onRemoteSessionEnded(),
      },
    })

    // Restore the volume they last listened at, so unmuting lands exactly
    // where they left off rather than jumping to 100%.
    const soundPref = getSoundPref()
    try {
      this.handle.player.volume(soundPref.volume)
    } catch {
      /* non-fatal */
    }

    const playerEl = this.handle.player.el()

    // A background preview owns the player for ~2s at a time. Without this, a
    // click that lands inside that window was undone by the refresh pausing
    // again a moment later — the viewer pressed play, nothing happened, and
    // they pressed again. Capture phase, so we hand the player back BEFORE
    // the button's own handler runs.
    const onPointerIntent = (ev: Event): void => {
      if (!isPlayGesture(ev.target)) return
      this.endPreviewForGesture()
    }
    playerEl.addEventListener('pointerdown', onPointerIntent, true)
    playerEl.addEventListener('keydown', onPointerIntent, true)

    // Arming happens on `click`, not `pointerdown`: the starting state makes
    // the big play button click-through (it becomes a spinner), so arming it
    // mid-gesture would stop the very click that asked for playback.
    const onPlayIntent = (ev: Event): void => this.armStartingFeedbackFor(ev.target)
    playerEl.addEventListener('click', onPlayIntent, true)
    playerEl.addEventListener('keydown', onPlayIntent, true)

    this.overlays.mountTitleBar(playerEl)
    this.overlays.mountPausedOverlay(playerEl)
    // After the chrome, so the toggle finds a built control bar.
    this.chat?.mount(playerEl)
    this.overlays.mountUnmutePill(playerEl, () => {
      if (!this.handle) return
      this.handle.player.muted(false)
      this.handle.player.volume(getSoundPref().volume)
      this.hasBeenUnmuted = true
      setSoundPref({ unmuted: true, volume: getSoundPref().volume })
      this.overlays.setUnmuteVisible(false)
      this.overlays.setTapToUnmute(false)
    })

    const syncMuteState = () => {
      if (!this.handle) return
      // Priming and background refreshes are not the viewer watching: no pill,
      // no tap layer. Forcing them OFF (rather than returning early) matters —
      // the pause that ENDS a preview arrives while the flag is still set, so
      // an early return could leave the full-surface tap layer on screen over
      // a paused player, swallowing every click on the play button.
      if (this.priming || this.refreshing) {
        this.overlays.setUnmuteVisible(false)
        this.overlays.setTapToUnmute(false)
        return
      }
      const p = this.handle.player
      const isMuted = (p.muted() ?? false) || (p.volume() ?? 1) === 0
      // "They have had sound" means audio actually reached them — i.e. NOT
      // paused. During the initial autoplay negotiation the element is briefly
      // unmuted-but-paused (we try audible, get rejected, then mute and retry);
      // counting that as a real unmute suppressed the pill's nudge and the
      // tap-to-unmute layer on every subsequent visit.
      if (!isMuted && !p.paused()) {
        this.hasBeenUnmuted = true
      }
      // The LEVEL, though, is worth remembering whenever they set it — including
      // while paused, which is exactly when people reach for the volume slider.
      // (Tying the level to `!paused` above meant a volume set on a paused
      // player was silently forgotten.)
      const vol = p.volume() ?? 1
      if (!p.muted() && vol > 0) {
        setSoundPref({ unmuted: getSoundPref().unmuted || this.hasBeenUnmuted, volume: vol })
      }
      // The pill shows any time the player is silent — including a mute the
      // viewer chose themselves, so there is always an obvious way back to
      // sound. The tap-anywhere layer and the looping nudge stay scoped to
      // the first muted playback, so clicking the picture keeps pausing once
      // the viewer has had sound.
      const silent = isMuted && !p.paused()
      this.overlays.setUnmuteVisible(silent)
      this.overlays.setTapToUnmute(silent && !this.hasBeenUnmuted)
    }
    this.handle.player.on(['volumechange', 'playing', 'pause'], syncMuteState)

    this.handle.player.on('play', () => {
      // Human-initiated play only: priming and background refreshes must not
      // flash the starting spinner or reveal the pre-start control bar.
      if (!this.priming && !this.refreshing && !this.handle?.player.hasClass('lp-scrubbing')) {
        this.handle?.player.addClass('lp-starting')
        this.startingFrom = this.handle?.player.currentTime() ?? 0
        this.armStartingStall()
        // No point spinning at a viewer the browser already knows is offline.
        if (navigator.onLine === false) {
          this.goDisconnected()
          return
        }
      }
      // A preview must not decide where real playback starts: first play after
      // priming jumps to the live edge (no-op for VOD, DVR seeks unaffected).
      if (!this.resumeFromLiveEdge) return
      this.resumeFromLiveEdge = false
      this.handle?.seekToLiveEdge()
    })

    // The starting state ends only when frames truly render — checked on
    // every signal that playback state advanced (see maybeClearStarting).
    this.handle.player.on(['seeked', 'timeupdate', 'canplaythrough'], () => this.maybeClearStarting())

    if (this.config.autoplay || this.hasStartedOnce) {
      // Autoplay embeds, and rebuilds/source switches after the viewer
      // already started watching, resume playback directly.
      void this.handle.play()
    } else {
      // Pre-start: no control bar until the viewer plays for the first time
      this.handle.player.addClass('lp-pre-start')
      if (!this.config.audioOnly) {
        // Paint the current frame behind the pre-start overlay.
        void this.primePreview()
      }
    }
  }

  /**
   * Redraw the two config-driven chrome layers on a live player. The LIVE
   * badge and the viewer pill appear in both the title bar and the pause
   * overlay, and both layers are built once at attach time, so a config
   * change that touches either has to rebuild both.
   *
   * Pause overlay first: detaching it leaves its viewer pill disconnected,
   * and mountTitleBar prunes disconnected pills before registering new ones.
   */
  /**
   * The chat panel exists only once watch-guard has said yes: the session id
   * IS the authorisation the chat app checks, so there is nothing to build
   * before a grant and nothing to build at all for a refused viewer.
   */
  private ensureChat(sessionId: string): ChatPanel | null {
    if (this.chat) return this.chat
    if (!this.config.chatEnabled || !sessionId) return null
    this.chat = new ChatPanel(this.container, {
      embedId: this.config.embedId,
      sessionId,
    })
    return this.chat
  }

  /**
   * Bring the chat into line with a config that changed under us.
   *
   * The panel is built from a GRANT, which arrives at boot and then only on
   * renewal — but chatEnabled is presentation, and presentation changes land
   * on the 30s config poll. Without this, switching chat on in the dashboard
   * did nothing until the next renewal or a reload, and switching it off left
   * the panel up with a live socket in a room the broadcaster had just
   * disabled. Off is the direction that matters: it is the one where the
   * player would be contradicting an instruction it had already received.
   */
  private syncChat(): void {
    if (!this.config.chatEnabled) {
      if (!this.chat) return
      this.log('chat disabled by config')
      this.chat.dispose()
      this.chat = null
      return
    }

    if (!this.chat) {
      if (!this.sessionId) return // no grant yet; adoptGrant will build it
      this.log('chat enabled by config')
      const chat = this.ensureChat(this.sessionId)
      if (chat && this.handle) chat.mount(this.handle.player.el() as Element)
    }
  }

  /** An embed poster the player should show, or null. http only: the value is
   *  a URL from an API and goes straight into a DOM attribute. */
  private posterUrl(): string | null {
    const url = this.config.posterUrl
    return url && url.startsWith('http') ? url : null
  }

  /** Push a changed poster onto the live player. video.js owns the element it
   *  renders it into, so setting the attribute ourselves would not show. */
  private applyPoster(): void {
    if (!this.handle) return
    try {
      this.handle.player.poster(this.posterUrl() ?? '')
    } catch {
      /* player disposed mid-poll */
    }
  }

  private remountChrome(): void {
    if (!this.handle) return
    const el = this.handle.player.el() as Element
    this.overlays.mountPausedOverlay(el)
    this.overlays.mountTitleBar(el)
    this.chat?.mount(el)
  }

  /**
   * The viewer clicked while a background refresh was driving the player.
   * The refresh is ALREADY playing the stream — so rather than pause and make
   * them click again, promote it to real playback: restore their sound, drop
   * the preview presentation, and keep going.
   *
   * Pausing here looked tidier but broke the very click that triggered it:
   * video.js hides the big play button while playing, so the button vanished
   * between mousedown and mouseup and the click retargeted to the picture —
   * the button's own handler never ran, which is exactly the "click three or
   * four times before it plays" symptom.
   */
  /**
   * Autoplay was refused and even the muted retry failed, so nothing is going
   * to play until the viewer asks for it.
   *
   * That is the same situation a no-autoplay embed is in before first play,
   * and until now it did not look like it: the config said autoplay, so the
   * stage never got `no-autoplay` and the player never got `lp-pre-start`,
   * and the viewer was left with video.js's bare play button over a black
   * rectangle, the stream's name shrunk into the top-left corner, and no sign
   * that the player was waiting for them rather than broken. The same embed
   * with autoplay switched OFF showed the full paused presentation. One
   * situation, two first impressions, decided by something the viewer cannot
   * see and did not choose.
   *
   * Adopting the same two classes gives it the same presentation, and they
   * are cleared by the existing first-play paths, so nothing else has to know
   * this state ever happened.
   *
   * No preview priming here, unlike the configured case: priming plays muted
   * to paint a frame, and we only reach this branch BECAUSE muted playback was
   * already refused.
   */
  private onAutoplayBlocked(): void {
    this.log('autoplay blocked — waiting for user gesture')
    if (!this.handle) return
    this.container.classList.add('no-autoplay')
    this.handle.player.addClass('lp-pre-start')
  }

  private endPreviewForGesture(): void {
    if (!this.refreshing || !this.handle) return
    this.log('viewer interacted during preview refresh — continuing as real playback')
    this.refreshing = false
    const p = this.handle.player
    p.removeClass('lp-refreshing')
    p.removeClass('lp-pre-start')
    if (this.refreshRestore) {
      try {
        p.muted(this.refreshRestore.muted)
        p.volume(this.refreshRestore.volume)
      } catch {
        /* non-fatal */
      }
    }
    this.refreshRestore = null
    this.hasStartedOnce = true
    this.userPaused = false
    // Their click must not now toggle off the playback it just took over.
    this.handle.suppressNextClick()
  }

  private destroyPlayer(): void {
    this.handle?.dispose()
    this.handle = null
    this.activeSource = null
  }

  /**
   * Keep a paused player's picture — and its buffer — sitting on the live
   * edge. Without this the frozen frame ages: a viewer who lands on the page
   * and plays two minutes later would resume from a stale position and have
   * to buffer forward. A short muted play at the live edge repaints the
   * current scene and leaves the buffer warm, so pressing play is immediate
   * and genuinely live.
   *
   * Runs only while paused, never while the viewer is actually watching.
   */
  private async refreshLivePreview(): Promise<void> {
    if (!this.handle || this.refreshing || this.priming || this.resolveBusy) return
    // A poster covers the paused frame, so refreshing that frame is a seek and
    // a segment fetch spent on something no one will ever see.
    if (this.posterUrl()) return
    if (this.state === 'offline' || this.state === 'stopped') return
    if (!this.primaryLive && !this.fallbackLive) return
    if (document.visibilityState === 'hidden') return // don't burn data in a background tab

    const p = this.handle.player
    if (!p.paused()) return // the viewer is watching — leave them alone

    this.refreshing = true
    // Freeze the paused presentation for the duration: without this the viewer
    // sees the pause overlay drop out, a spinner flash and the control bar
    // appear every few seconds as the preview catches up to the live edge.
    p.addClass('lp-refreshing')
    const wasMuted = p.muted() ?? false
    const wasVolume = p.volume() ?? 1
    this.refreshRestore = { muted: wasMuted, volume: wasVolume }

    try {
      p.muted(true)
      this.handle.seekToLiveEdge()
      await playBounded(this.handle)
      // Just long enough to decode a frame at the new position.
      await new Promise<void>((resolve) => {
        let settled = false
        const finish = (): void => {
          if (settled) return
          settled = true
          p.off('timeupdate', onTick)
          resolve()
        }
        const onTick = (): void => finish()
        p.on('timeupdate', onTick)
        setTimeout(finish, 1500)
      })
    } catch {
      // Autoplay blocked or source not ready — the stale frame stays; harmless.
    } finally {
      const handle = this.handle
      // The viewer took the player back mid-refresh — endPreviewForGesture()
      // has already restored and paused it, so touch nothing.
      if (!this.refreshing) {
        this.refreshRestore = null
      } else if (!handle) {
        this.refreshing = false
        this.refreshRestore = null
      } else {
        handle.pause()
        handle.player.muted(wasMuted)
        handle.player.volume(wasVolume)

        // `playing`/`timeupdate` events queued before the pause can still land
        // after this point. Dropping the guard immediately let onPlaying() clear
        // userPaused, after which the watchdog "recovered" a deliberately paused
        // player — i.e. the video resumed itself. Hold the guard past the event
        // tail, then assert the paused state actually stuck.
        setTimeout(() => {
          if (!this.refreshing) return // handed back to the viewer meanwhile
          this.refreshing = false
          this.refreshRestore = null
          if (this.handle && !this.handle.player.paused()) {
            this.log('preview refresh: playback resumed unexpectedly — re-pausing')
            this.handle.pause()
          }
          // Only hand the presentation back once the player is settled paused
          // again, so the overlay never blinks on the way out either.
          this.handle?.player.removeClass('lp-refreshing')
        }, 600)
      }
    }
  }

  /**
   * Muted prime-and-pause: VHS never downloads media segments for a LIVE
   * source before play (regardless of preload), so a paused pre-start player
   * has nothing to paint. Briefly playing muted fetches the live segments and
   * paints the current frame, then we pause and hand control to the viewer.
   */
  private async primePreview(): Promise<void> {
    if (!this.handle) return
    const p = this.handle.player
    const playerEl = p.el() as HTMLElement
    this.priming = true

    // The lp-priming class makes the player LOOK paused for the whole preview:
    // spinner hidden, control bar hidden, big play + overlay visible.
    p.addClass('lp-priming')
    this.log('priming frame preview (muted play → pause, visually silent)')

    // If the viewer clicks during priming they mean "play" — don't yank it
    // back to paused underneath their click.
    let userWantsPlay = false
    const onGesture = () => {
      userWantsPlay = true
    }
    playerEl.addEventListener('pointerdown', onGesture)

    try {
      p.muted(true)
      await playBounded(this.handle)

      // Wait until a frame has actually advanced (or give up after 4s)
      await new Promise<void>((resolve) => {
        let settled = false
        const finish = () => {
          if (settled) return
          settled = true
          p.off('timeupdate', onTick)
          resolve()
        }
        const onTick = () => {
          if ((p.currentTime() ?? 0) > 0) finish()
        }
        p.on('timeupdate', onTick)
        setTimeout(finish, 4000)
      })

      if (!this.handle) return // disposed while priming

      if (userWantsPlay) {
        // Their click was a real gesture — continue playing, audibly.
        this.hasStartedOnce = true
        p.removeClass('lp-pre-start')
        p.muted(false)
        p.volume(1)
        this.log('preview upgraded to playback (user gesture during priming)')
        return
      }

      this.userPaused = true // keep the watchdog from auto-resuming the preview
      this.handle.pause()
      this.resumeFromLiveEdge = true

      // The real, user-initiated play should be audible
      p.muted(false)
      p.volume(1)
      this.log('frame preview ready')
    } catch {
      // Even muted autoplay is blocked — poster/overlay remain the fallback.
      this.log('frame preview unavailable (muted autoplay blocked)')
    } finally {
      playerEl.removeEventListener('pointerdown', onGesture)
      try {
        p.removeClass('lp-priming')
      } catch {
        /* player may be disposed */
      }
      this.priming = false
    }
  }

  // ── player events ────────────────────────────────────────────────────────

  private onPlaying(): void {
    // A background refresh is not the viewer watching — it must not clear the
    // paused state or move the state machine.
    if (this.refreshing) return
    this.rebuildCount = 0
    this.mediaErrorCount = 0
    this.renewedForAuthError = false
    this.sawMediaAuthError = false
    this.watchdogStrikes = 0
    this.userPaused = false
    this.overlays.hideOffline()
    this.maybeClearStarting()
    this.everPlayed = true
    this.transition('playing', 'media playing')
  }

  /**
   * End the "starting" spinner state only when playback is REALLY rendering:
   * not paused, not mid-seek (the jump to the live edge fires right after
   * play), and with decodable data. A live resume often fires a premature
   * `playing` from stale buffer before the live-edge seek — clearing on that
   * event alone hides the spinner during the actual buffering.
   */
  private maybeClearStarting(): void {
    if (this.priming || this.refreshing || !this.handle) return
    const p = this.handle.player
    const el = p.el()
    if (!el.classList.contains('lp-starting') && !el.classList.contains('lp-pre-start')) return
    try {
      if (p.paused() || p.seeking()) return
      if ((p.readyState() ?? 0) < 3) return
      // A stalled player can sit at readyState 4 on a stale buffer and claim
      // it is playing. Only movement past where play was pressed proves that
      // frames are actually being delivered.
      if ((p.currentTime() ?? 0) <= this.startingFrom + 0.05) return
    } catch {
      return
    }
    this.hasStartedOnce = true
    this.startingStallRebuilds = 0
    this.clearStartingStall()
    p.removeClass('lp-starting')
    p.removeClass('lp-pre-start')
  }

  /**
   * Show the starting ring the moment the viewer asks for playback, rather
   * than waiting for the media element's `play` event.
   *
   * A media element that woke up unable to start never fires that event at
   * all — the click looked completely ignored, with no spinner, no ring and
   * nothing in the picture to say the player was even alive.
   */
  private armStartingFeedbackFor(target: EventTarget | null): void {
    if (!this.handle || this.priming || this.refreshing) return
    const el = target instanceof Element ? target : null
    // Only for the controls that mean "play": not the gear, not the volume.
    if (!el?.closest('.vjs-big-play-button, .vjs-play-control, .vjs-tech, video')) return
    const p = this.handle.player
    if (!p.paused() || p.hasClass('lp-scrubbing')) return

    p.addClass('lp-starting')
    this.startingArmedAt = Date.now()
    this.startingFrom = p.currentTime() ?? 0
    this.armStartingStall()
    this.scheduleStartingSettle()
  }

  /**
   * A double-click (fullscreen) never starts playback, so the ring has to come
   * back off once the double-click window has passed with the player still
   * paused.
   *
   * The trap is that "still paused" is also true while the player is doing
   * exactly what the viewer asked for. Resolving a source is a watch-guard
   * round trip plus token signing, and the media element stays paused for the
   * whole of it. On a slow connection that window runs to seconds, and taking
   * the ring down inside it is the deceptive case: the viewer gets a plain
   * play button back while the player is still working on their click, reads
   * that as the click being ignored, and presses again. So hold the ring while
   * a resolve or a preview is in flight and re-check when it lands.
   */
  private scheduleStartingSettle(attempt = 0): void {
    const armedAt = this.startingArmedAt
    setTimeout(() => {
      // A newer click armed its own settle check; this one is stale.
      if (!this.handle || this.startingArmedAt !== armedAt) return
      const p = this.handle.player
      if (!p.el().classList.contains('lp-starting')) return
      // Playback took. The ring now belongs to maybeClearStarting, the pause
      // handler, the error handler and the stall timer.
      if (!p.paused()) return
      if (this.priming || this.refreshing || this.resolveBusy) {
        if (attempt < STARTING_SETTLE_RETRIES) this.scheduleStartingSettle(attempt + 1)
        return
      }
      this.clearStartingStall()
      p.removeClass('lp-starting')
    }, DOUBLE_CLICK_SETTLE_MS)
  }

  /**
   * A media element that slept through a laptop lid closing often comes back
   * unable to start: play() resolves, nothing renders, and the viewer clicks
   * again and again. Give it one bounded chance, then rebuild the player on a
   * freshly resolved source — the ring keeps spinning throughout, so the
   * player looks alive rather than ignored.
   */
  private armStartingStall(): void {
    this.clearStartingStall()
    this.startingStallTimer = setTimeout(() => {
      this.startingStallTimer = null
      if (this.state === 'stopped' || !this.handle) return
      if (!this.handle.player.el().classList.contains('lp-starting')) return
      if (this.priming || this.refreshing || this.resolveBusy) return
      if (this.startingStallRebuilds >= MAX_STARTING_REBUILDS) {
        // Rebuilding twice did not help: stop spinning at the viewer and give
        // them the play button back rather than an animation that lies.
        this.log('playback still not starting — handing control back to the viewer')
        this.handle.player.removeClass('lp-starting')
        this.startingStallRebuilds = 0
        return
      }
      this.startingStallRebuilds++
      this.log(`playback never started — rebuilding the player (${this.startingStallRebuilds})`)
      this.hasStartedOnce = true // the rebuilt player resumes without a second click
      this.resumeFromLiveEdge = true
      void this.resolveAndPlay(this.activeSource ?? 'primary')
    }, STARTING_STALL_MS)
  }

  private clearStartingStall(): void {
    if (!this.startingStallTimer) return
    clearTimeout(this.startingStallTimer)
    this.startingStallTimer = null
  }

  /**
   * A cast/AirPlay session ended. If the receiver simply vanished (TV powered
   * off) the Chromecast tech is still selected but has no session, leaving a
   * dead black player. Rebuild on the local tech and pick playback back up
   * where the stream is now — the viewer keeps watching, just here instead.
   */
  private onRemoteSessionEnded(): void {
    if (this.state === 'stopped') return
    this.log('remote session ended — restoring local playback')
    // A fresh player re-runs tech selection; with no live cast session the
    // chromecast tech declines and html5 takes over.
    this.userPaused = false
    this.resumeFromLiveEdge = true
    void this.resolveAndPlay(this.activeSource ?? 'primary')
  }

  private onUserPause(): void {
    this.userPaused = true
    // If they bail out mid-start, drop the spinner and show the paused UI
    this.handle?.player.removeClass('lp-starting')
    this.log('user paused')
  }

  private onPlayerError(message: string): void {
    if (this.state === 'stopped') return
    this.log(`player error: ${message}`)

    // Whatever happens next, the ring stops now. A spinner over a failure is
    // the player lying about being busy.
    this.handle?.player.removeClass('lp-starting')
    this.clearStartingStall()

    // Nothing is live, so this is not a failure to recover from. Tearing the
    // player down is the point: leaving it mounted puts a big play button on
    // top of the offline screen, which is what the viewer sees as "it is
    // broken AND it is telling me to press play".
    if (!this.primaryLive && !this.fallbackLive) {
      this.mediaErrorCount = 0
      this.goOffline('media error with nothing live')
      return
    }

    this.mediaErrorCount++

    if (this.mediaErrorCount > MAX_MEDIA_ERRORS) {
      this.goMediaError()
      return
    }

    // One error is a blip — a dropped segment, a renegotiated connection — and
    // announcing it would be noisier than fixing it. Past that, the viewer has
    // been looking at a stalled picture long enough to deserve the truth.
    if (this.mediaErrorCount > QUIET_MEDIA_ERRORS) this.announceTrouble()

    this.scheduleRestart('player error')
  }

  /**
   * Say the true thing about why there is no picture.
   *
   * This used to be one hardcoded "Trying to get the stream back" card, shown
   * for any run of media errors. On a stream that had never gone live, that is
   * a lie in two directions at once: nothing broke, and there is no "back" to
   * get to. A customer with a holding page up a week before a funeral service
   * read it as a fault, and spent a morning changing settings to fix a player
   * that was working exactly as intended.
   *
   * Three different situations, three different answers:
   *
   *   nothing live      not a failure at all. The broadcaster's own offline
   *                     presentation, which `showOfflineText: false` is
   *                     allowed to suppress down to just the cover image.
   *   live, never played  we are still getting it, not getting it BACK.
   *   live, played here   the feed genuinely dropped. This is the case
   *                     uninterrupted mode exists for.
   */
  private announceTrouble(): void {
    // Callers tear down before reaching here when nothing is live. Kept as a
    // guard so a future caller cannot reintroduce a reconnect card over a
    // stream that never started.
    if (!this.primaryLive && !this.fallbackLive) {
      this.goOffline('nothing live')
      return
    }

    if (this.everPlayed) {
      this.overlays.showOffline({
        tone: 'retrying',
        label: 'Reconnecting',
        title: 'Trying to get the stream back',
        subtitle: 'This usually takes a few seconds',
      })
      return
    }

    this.overlays.showOffline({
      tone: 'retrying',
      label: 'Connecting',
      title: 'Getting the stream',
      subtitle: 'One moment',
    })
  }

  private onHttpError(url: string, status: number): void {
    if (this.state === 'stopped') return

    // A 404 on the active playlist usually means the stream ended or the CDN
    // path changed — re-resolve rather than hammering the dead URL.
    if (status === 404 && url.includes('.m3u8')) {
      this.scheduleRestart('playlist 404')
      return
    }

    // 401/403 on media is the edge rejecting our token, and the overwhelmingly
    // likely cause is that it expired or the viewer's IP moved under them —
    // both of which a renewal fixes. It is emphatically NOT "the format is not
    // supported", which is what video.js would otherwise tell them.
    //
    // Once, though. If a fresh token is refused too, the answer is no, and
    // renewing in a loop would just be a slower way of failing.
    if (status === 401 || status === 403) this.sawMediaAuthError = true

    if ((status === 401 || status === 403) && !this.renewedForAuthError) {
      this.renewedForAuthError = true
      this.log(`media ${status} — token refused, renewing once before giving up`)
      void this.renewAndRetry()
      return
    }

    if (status === 401 || status === 403) {
      this.goMediaError('access')
    }
  }

  /** A refused token, met with a fresh one. */
  private async renewAndRetry(): Promise<void> {
    await this.renewNow()
    if (this.state === 'stopped' || this.state === 'denied') return
    void this.resolveAndPlay(this.activeSource ?? 'primary')
  }

  /**
   * We have stopped trying. Say what is true, and leave the viewer something to
   * press — a manual retry costs us one request and is the difference between a
   * player that failed and a player that is broken.
   */
  private goMediaError(kind: 'playback' | 'access' = 'playback'): void {
    // A 403 on the media and a generic MEDIA_ERR arrive together — the edge
    // refuses the request, then the element reports it as an unplayable source.
    // The HTTP status is the specific signal and the element's is the vague
    // one, so whichever call lands here first, the specific answer wins.
    if (this.sawMediaAuthError) kind = 'access'

    this.log(`giving up after ${this.mediaErrorCount} media errors (${kind})`)

    // Running out of attempts against a stream that is not live is not a
    // playback failure, and offering "Try again" invites the viewer to keep
    // pressing a button that cannot help. The broadcast simply has not
    // started, so say that instead.
    if (!this.primaryLive && !this.fallbackLive) {
      this.mediaErrorCount = 0
      this.goOffline(`media error with nothing live (${kind})`)
      return
    }

    this.clearTimers()
    this.destroyPlayer()
    this.transition('offline', `media error (${kind})`)

    const copy =
      kind === 'access'
        ? {
            title: 'We could not verify your access',
            subtitle: 'Reload the page, or try again in a moment',
          }
        : {
            title: 'This stream would not play',
            subtitle: 'Check your connection, then try again',
          }

    this.overlays.showOffline({
      tone: 'error',
      label: kind === 'access' ? 'Not authorised' : 'Playback failed',
      ...copy,
      action: { label: 'Try again', onClick: () => this.retryFromError() },
    })
  }

  /** The viewer pressed "Try again": forget the failure history and start over. */
  private retryFromError(): void {
    if (this.state === 'stopped') return
    this.log('viewer asked to retry')
    this.mediaErrorCount = 0
    this.rebuildCount = 0
    this.renewedForAuthError = false
    this.sawMediaAuthError = false
    this.overlays.showOffline({
      tone: 'retrying',
      label: 'Reconnecting',
      title: 'Getting the stream',
      subtitle: 'One moment',
    })
    this.startPollers()
    void this.resolveAndPlay(this.activeSource ?? 'primary')
  }

  /** All recovery funnels through here: backoff, then re-resolve. */
  private scheduleRestart(reason: string): void {
    // `resolveBusy` is the real "a rebuild is already in flight" signal. The
    // state is NOT: it stays 'resolving' from the moment we attach until media
    // actually plays, so a source that errors before ever playing — an expired
    // token, a codec the browser lacks, a 403 — would land here in 'resolving'
    // and be silently dropped. That is the player that spins forever having
    // given up on the first try.
    if (this.restartTimer || this.resolveBusy || this.state === 'stopped') return
    const delay = Math.min(1000 * 2 ** this.rebuildCount, MAX_REBUILD_DELAY_MS)
    this.rebuildCount++
    this.log(`restart in ${delay}ms (${reason}, attempt ${this.rebuildCount})`)
    this.restartTimer = setTimeout(() => {
      this.restartTimer = null
      void this.resolveAndPlay(this.activeSource ?? 'primary')
    }, delay)
  }

  /**
   * Offline because WE cannot reach the network — not because the broadcast
   * ended. Same teardown, different words, and the `online` listener plus the
   * status poller bring it back on their own.
   */
  /** True when the element is actually rendering media, not merely mounted. */
  private isMediaAdvancing(): boolean {
    if (this.state !== 'playing' || !this.handle) return false
    const probe = this.handle.probe()
    return !probe.paused && probe.currentTime > this.lastProbeTime
  }

  private goDisconnected(): void {
    if (this.state === 'offline') return

    // The status API is advisory. If media is still arriving, an outage over
    // there says nothing about this viewer's connection, and tearing down a
    // player that is visibly working is the worse error of the two.
    if (navigator.onLine !== false && this.isMediaAdvancing()) {
      this.log('status API unreachable but media is still advancing — staying up')
      return
    }

    this.clearTimers()
    this.destroyPlayer()
    this.overlays.showOffline({
      label: 'No connection',
      title: 'You appear to be offline',
      subtitle: 'We will pick the stream back up as soon as you reconnect',
    })
    this.transition('offline', 'connection lost')
  }

  /**
   * Policy said no. Terminal for this configuration: the pollers stop, the
   * player is torn down, and the viewer gets a branded explanation rather than
   * a blank frame. Only a password retry (or a reload) leaves this state.
   */
  private goDenied(reason: api.DenialReason, message: string): void {
    this.log(`denied: ${reason}`)
    this.clearTimers()
    this.destroyPlayer()
    this.overlays.setStreamLive(false)
    this.stopPollers()
    // Authorisation was withdrawn: the room goes with it, not just the video.
    this.chat?.dispose()
    this.chat = null
    this.transition('denied', reason)

    // A different answer than last time means the situation moved, so the
    // backoff the old one earned no longer applies.
    if (reason !== this.deniedReason) this.denialRecheckDelay = DENIAL_RECHECK_MS
    this.deniedReason = reason

    if (reason === 'password_required' || reason === 'password_incorrect') {
      this.promptForPassword(reason === 'password_incorrect' ? message : null)
      return
    }

    // Tone 'error', not the default 'offline': showOfflineText is the
    // broadcaster saying "don't announce that I'm not live", which is not
    // permission to leave a refused viewer looking at a black box.
    this.overlays.showOffline({ ...denialCopy(reason, message), tone: 'error' })
    if (RECHECKABLE_DENIALS.has(reason)) this.scheduleDenialRecheck()
  }

  private scheduleDenialRecheck(): void {
    if (this.denialRecheckTimer) return
    const delay = this.denialRecheckDelay
    this.denialRecheckDelay = Math.min(delay * 2, DENIAL_RECHECK_MAX_MS)
    this.denialRecheckTimer = setTimeout(() => {
      this.denialRecheckTimer = null
      void this.recheckDenial()
    }, delay)
  }

  /**
   * Ask again whether this viewer may watch.
   *
   * Only authorize can answer that. The config poll carries presentation, not
   * policy, and it never runs here anyway: a denied viewer starts no pollers,
   * because the viewers ping doubles as the concurrency heartbeat and someone
   * who was refused must not hold a slot. So without this the blocked card
   * would outlive the block.
   */
  private async recheckDenial(): Promise<void> {
    if (this.state !== 'denied' || this.denialRecheckBusy) return
    if (!this.deniedReason || !RECHECKABLE_DENIALS.has(this.deniedReason)) return

    this.denialRecheckBusy = true
    this.lastDenialRecheckAt = Date.now()
    if (this.denialRecheckTimer) {
      clearTimeout(this.denialRecheckTimer)
      this.denialRecheckTimer = null
    }

    try {
      const result = await api.authorize(this.config.embedId, {
        framed: window.self !== window.top,
        audioOnly: this.config.audioOnly,
        sessionId: recallSession(this.config.embedId),
      })

      if (result.ok) {
        this.log(`denial lifted (was ${this.deniedReason})`)
        this.deniedReason = null
        this.denialRecheckDelay = DENIAL_RECHECK_MS
        // The denial we rendered may have carried branding only (the leak
        // rule: an unauthorised site must not learn the stream's content), so
        // this is the first full config we have seen. Replace it outright
        // rather than diffing the real thing against a stub.
        this.config = buildConfig(result.config, {
          embedId: this.config.embedId,
          audioOnly: this.config.audioOnly,
        })
        this.overlays.updateConfig(this.config)
        this.overlays.hideOffline()
        this.adoptGrant(result.playback)
        this.startPollers()
        void this.resolveAndPlay('primary')
        return
      }

      if (result.kind === 'denied' && result.reason !== this.deniedReason) {
        // Still refused, but for a different reason: re-render, which resets
        // the backoff and re-arms the recheck against the new reason.
        this.goDenied(result.reason, result.message)
        return
      }
    } finally {
      this.denialRecheckBusy = false
    }

    // Same answer, or the service was unreachable. Wait longer, ask again.
    this.scheduleDenialRecheck()
  }

  /** The gate owns the surface until the viewer gets it right. */
  private promptForPassword(errorMessage: string | null): void {
    if (!this.passwordGate) {
      this.passwordGate = mountPasswordPrompt(this.container, {
        title: this.config.title,
        checkLive: () => api.fetchLiveStatus(this.config.embedId),
        onSubmit: async (password) => {
          const result = await api.authorize(this.config.embedId, {
            framed: window.self !== window.top,
            audioOnly: this.config.audioOnly,
            password,
            sessionId: recallSession(this.config.embedId),
          })

          if (result.ok) {
            this.passwordGate?.dismiss()
            this.passwordGate = null
            this.adoptGrant(result.playback)
            this.startPollers()
            void this.resolveAndPlay('primary')
            return
          }

          if (result.kind === 'denied') {
            if (result.retryAfterSeconds) {
              this.passwordGate?.setCooldown(result.retryAfterSeconds)
              return
            }
            if (result.reason === 'password_incorrect' || result.reason === 'password_required') {
              this.passwordGate?.setError(result.message || 'Incorrect password')
              return
            }
            // Something else refused it now — swap the gate for that state.
            this.passwordGate?.dispose()
            this.passwordGate = null
            this.goDenied(result.reason, result.message)
            return
          }

          this.passwordGate?.setError('Could not reach the server. Try again.')
        },
      })
    }
    if (errorMessage) this.passwordGate.setError(errorMessage)
  }

  /**
   * Tokens expire mid-session. Renew shortly BEFORE `renewAfter` and swap the
   * source; a denial here means stop, not carry on with the old token.
   */
  private scheduleRenewal(grant: api.PlaybackGrant): void {
    if (this.renewTimer) clearTimeout(this.renewTimer)
    this.renewTimer = null
    if (this.config.preloadHlsUrl) return // dev override: nothing to renew

    const at = grant.renewAfter ? Date.parse(grant.renewAfter) : NaN
    if (!Number.isFinite(at)) return
    const delay = Math.max(5_000, at - Date.now())

    this.renewTimer = setTimeout(() => {
      this.renewTimer = null
      void this.renewNow()
    }, delay)
  }

  private async renewNow(): Promise<void> {
    if (this.state === 'stopped' || this.state === 'denied' || !this.sessionId) return
    this.log('renewing playback token')

    const result = await api.renew(this.config.embedId, this.sessionId)

    if (result.ok) {
      this.adoptGrant(result.playback)
      this.pendingGrant = null
      // Swap the source silently: the viewer is watching, and the rebuild path
      // is the one already used for source switches and cast recovery.
      if (this.state === 'playing' || this.handle) {
        this.renewedUrl = this.applyRewind(result.playback.hlsUrl, 'primary')
      }
      return
    }

    if (result.kind === 'denied') {
      // needsPassword tells us a re-prompt is the right state rather than a
      // generic blocked notice.
      this.goDenied(result.reason, result.message)
      return
    }

    // Could not reach the service: keep playing on the token we have and try
    // again shortly — it is still valid until `expiresAt`.
    this.log('renewal unreachable — retrying shortly')
    this.renewTimer = setTimeout(() => {
      this.renewTimer = null
      void this.renewNow()
    }, RENEW_RETRY_MS)
  }

  private goOffline(reason: string): void {
    this.clearTimers()
    this.destroyPlayer()
    // A prime or a refresh that died with the player must not leave its flag
    // set: both of them gate later behaviour, and a stuck `priming` is why a
    // failed preview kept the pre-start presentation on screen.
    this.priming = false
    this.refreshing = false
    this.overlays.setStreamLive(false)
    this.overlays.showOffline()
    this.transition('offline', reason)
    // Now that nothing is mounted, a deferred change costs nothing to take.
    this.maybeApplyPendingConfig()
    // No 1s re-check loop here: the status poller is already running and will
    // bring us back the moment a source reports live.
  }

  // ── watchdog ─────────────────────────────────────────────────────────────

  /** One watchdog. Only acts in `playing`, never fights the poller or switches. */
  private watchdogTick(): void {
    if (this.state !== 'playing' || !this.handle) return
    if (this.userPaused || this.pendingSwitch || this.restartTimer) return
    if (this.refreshing || this.priming) return // the preview owns the player
    if (!this.primaryLive && !this.fallbackLive) return // status logic owns this case

    const probe = this.handle.probe()

    if (probe.paused && !this.handle.isProgrammaticPause()) {
      // Paused but not by the user (e.g. browser throttling) — nudge it.
      void this.handle.play()
      return
    }

    if (probe.currentTime === this.lastProbeTime) {
      this.watchdogStrikes++
      this.log(`watchdog: no progress (strike ${this.watchdogStrikes}/${WATCHDOG_STRIKES})`)
      if (this.watchdogStrikes >= WATCHDOG_STRIKES) {
        this.watchdogStrikes = 0
        this.scheduleRestart('watchdog: playback stalled')
      }
    } else {
      this.watchdogStrikes = 0
    }
    this.lastProbeTime = probe.currentTime
  }

  // ── viewers ──────────────────────────────────────────────────────────────

  private async pollViewers(): Promise<boolean> {
    if (this.state !== 'playing') return true

    const params = { embedId: this.config.embedId, nsid: this.config.nsid, sessionId: this.sessionId }
    const revoked = await api.pingViewer(params)

    // Access withdrawn while they were watching. Stop now rather than let the
    // remaining token run out: "disabled" that keeps playing for another hour
    // is not disabled.
    if (revoked) {
      this.log(`access revoked mid-playback: ${revoked}`)
      this.goDenied(revoked, '')
      return true
    }

    if (this.config.showViewersCount) {
      const count = await api.fetchViewerCount(params)
      if (count !== null) this.overlays.setViewerCount(count)
    }
    return true
  }

  // ── misc ─────────────────────────────────────────────────────────────────

  private clearTimers(): void {
    if (this.pendingSwitch) {
      clearTimeout(this.pendingSwitch.timer)
      this.pendingSwitch = null
    }
    if (this.offlineGraceTimer) {
      clearTimeout(this.offlineGraceTimer)
      this.offlineGraceTimer = null
    }
    if (this.restartTimer) {
      clearTimeout(this.restartTimer)
      this.restartTimer = null
    }
    this.clearStartingStall()
    if (this.renewTimer) {
      clearTimeout(this.renewTimer)
      this.renewTimer = null
    }
    if (this.denialRecheckTimer) {
      clearTimeout(this.denialRecheckTimer)
      this.denialRecheckTimer = null
    }
  }

  private log(message: string): void {
    // Never suppress logs (v1 silenced videojs errors globally). Note the URL
    // is logged without its query string — the playback token lives there and
    // is a bearer credential.
    console.debug(`[player] ${message}`)
  }
}
